let exclusionClientId = 0;

const GENERAL_CATEGORY = "General";

const nextClientId = () => {
  exclusionClientId += 1;
  return `exclusion-item-${exclusionClientId}`;
};

const normalizeValue = (value) => value?.toString?.().trim?.() || "";

export const isGeneralExclusionCategory = (value) =>
  normalizeValue(value).toLowerCase() === GENERAL_CATEGORY.toLowerCase();

export const createEmptyExclusionItem = (overrides = {}) => ({
  clientId: nextClientId(),
  id: null,
  category: "",
  description: "",
  position: 1,
  spanish_translation: null,
  ...overrides,
});

export const isBlankExclusionItem = (item = {}) =>
  normalizeValue(item.category) === "" &&
  normalizeValue(item.description) === "";

export const normalizeExclusionItems = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  return [...items]
    .sort((left, right) => {
      const leftPosition = Number(left?.position || 0);
      const rightPosition = Number(right?.position || 0);
      return leftPosition - rightPosition;
    })
    .map((item, index) =>
      createEmptyExclusionItem({
        id: item?.id ?? null,
        category: item?.category || "",
        description: item?.description || "",
        position: index + 1,
        spanish_translation: item?.spanish_translation || null,
      }),
    );
};

export const serializeExclusionItems = (items = []) =>
  items
    .filter((item) => !isBlankExclusionItem(item))
    .map((item, index) => ({
      ...(item?.id ? { id: item.id } : {}),
      category: normalizeValue(item.category),
      description: normalizeValue(item.description),
      position: index + 1,
    }));

export const validateExclusionItems = (items = [], t) => {
  const nextErrors = {};

  items.forEach((item) => {
    const category = normalizeValue(item.category);
    const description = normalizeValue(item.description);

    const itemErrors = {};

    if (!category) {
      itemErrors.category = t(
        "discount-cards.create.form.errors.exclusion-category",
      );
    }

    if (!description) {
      itemErrors.description = t(
        "discount-cards.create.form.errors.exclusion-description",
      );
    }

    if (Object.keys(itemErrors).length > 0) {
      nextErrors[item.clientId] = itemErrors;
    }
  });

  return nextErrors;
};

export const updateExclusionItemAtIndex = (items, index, changes) =>
  items.map((item, itemIndex) =>
    itemIndex === index ? { ...item, ...changes } : item,
  );

export const removeExclusionItemAtIndex = (items, index) =>
  items
    .filter((_, itemIndex) => itemIndex !== index)
    .map((item, itemIndex) => ({ ...item, position: itemIndex + 1 }));

export const moveExclusionItem = (items, index, offset) => {
  const targetIndex = index + offset;

  if (targetIndex < 0 || targetIndex >= items.length) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(index, 1);
  nextItems.splice(targetIndex, 0, movedItem);

  return nextItems.map((item, itemIndex) => ({
    ...item,
    position: itemIndex + 1,
  }));
};

export const reorderExclusionItems = (items = [], fromIndex, toIndex) => {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length
  ) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);

  return nextItems.map((item, itemIndex) => ({
    ...item,
    position: itemIndex + 1,
  }));
};

export const groupExclusionItems = (items = []) => {
  const groups = [];
  const groupedItems = serializeExclusionItems(items);

  groupedItems.forEach((item) => {
    const normalizedCategory =
      normalizeValue(item.category) || GENERAL_CATEGORY;
    const existingGroup = groups.find(
      (group) =>
        group.category.toLowerCase() === normalizedCategory.toLowerCase(),
    );

    if (existingGroup) {
      existingGroup.items.push(item.description);
      return;
    }

    groups.push({
      category: normalizedCategory,
      items: [item.description],
    });
  });

  return groups;
};

export const buildExclusionDisclaimer = (items = [], fallbackValue = "") => {
  const groups = groupExclusionItems(items);

  if (groups.length === 0) {
    return fallbackValue || "";
  }

  return groups
    .map((group) => {
      if (isGeneralExclusionCategory(group.category)) {
        return group.items.join(", ");
      }

      return `${group.category}: ${group.items.join(", ")}`;
    })
    .join("\n");
};

export const mergeExclusionTranslationItems = (
  englishItems = [],
  translatedItems = [],
) =>
  englishItems.map((item) => {
    const translatedItem = translatedItems.find(
      (translation) => translation.id === item.id,
    );

    return {
      id: item.id,
      category:
        normalizeValue(translatedItem?.spanish_category) ||
        normalizeValue(item.category),
      description:
        normalizeValue(translatedItem?.spanish_description) ||
        normalizeValue(item.description),
      position: item.position,
    };
  });
